import React, { useState } from "react";

import {
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Text,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";

import axios from "axios";

// PHP handler shipped from /public into the build. Override in .env for local dev,
// e.g. REACT_APP_LMS_TRIAL_URL=http://localhost/iqspl/public/lms-trial.php
const TRIAL_URL =
  process.env.REACT_APP_LMS_TRIAL_URL ||
  `${process.env.PUBLIC_URL}/lms-trial.php`;

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  website: "", // honeypot, hidden from users
};

const TrialForm = ({ isOpen, onClose }) => {
  const [data, setData] = useState(emptyForm);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [lmsUrl, setLmsUrl] = useState("");

  const closeHandler = () => {
    if (isLoading) return;
    setData(emptyForm);
    setError("");
    setSuccessMessage("");
    setLmsUrl("");
    onClose();
  };

  const changeHandler = (e) => {
    setData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const res = await axios.post(TRIAL_URL, data);
      if (res.data.success) {
        setSuccessMessage(res.data.message);
        setLmsUrl(res.data.lmsUrl || "");
        setData(emptyForm);
      } else {
        setError(res.data.message || "Something went wrong.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Could not submit the request. Please try again."
      );
    }
    setIsLoading(false);
  };

  return (
    <Modal onClose={closeHandler} size={"md"} isOpen={isOpen} isCentered>
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader color={"quaternary"}>
          {successMessage ? "Success!" : "Try LMS Now – Free Access"}
        </ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />

        {successMessage ? (
          <>
            <ModalBody textAlign={"center"}>
              <CheckCircleIcon boxSize={14} color={"green.500"} mb={4} />
              <Text fontWeight={"semibold"} mb={2}>
                {successMessage}
              </Text>
              <Text color={"secondary"} fontSize={"sm"}>
                Please check your inbox (and spam folder) for your LMS login
                details.
              </Text>
            </ModalBody>
            <ModalFooter gap={3}>
              <Button variant={"ghost"} onClick={closeHandler}>
                Close
              </Button>
              {lmsUrl && (
                <Button
                  as={"a"}
                  href={lmsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  bg={"quaternary"}
                  color={"tertiary"}
                  _hover={{ opacity: 0.9 }}
                >
                  Go to LMS
                </Button>
              )}
            </ModalFooter>
          </>
        ) : (
          <form onSubmit={submitHandler}>
            <ModalBody>
              <Text color={"secondary"} fontSize={"sm"} mb={4}>
                Enter your details to receive LMS test access via email.
              </Text>

              <FormControl isRequired mb={3}>
                <FormLabel>Full Name</FormLabel>
                <Input
                  name="name"
                  value={data.name}
                  onChange={changeHandler}
                  maxLength={100}
                />
              </FormControl>

              <FormControl isRequired mb={3}>
                <FormLabel>Email ID</FormLabel>
                <Input
                  type="email"
                  name="email"
                  value={data.email}
                  onChange={changeHandler}
                  maxLength={150}
                />
              </FormControl>

              <FormControl isRequired mb={3}>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  type="tel"
                  name="phone"
                  value={data.phone}
                  onChange={changeHandler}
                  pattern="\+?[0-9 \-]{7,20}"
                  title="Enter a valid phone number"
                  maxLength={20}
                />
              </FormControl>

              <input
                type="text"
                name="website"
                value={data.website}
                onChange={changeHandler}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ display: "none" }}
              />

              {error && (
                <Text color={"red.500"} fontSize={"sm"}>
                  {error}
                </Text>
              )}
            </ModalBody>

            <ModalFooter gap={3}>
              <Button
                variant={"ghost"}
                onClick={closeHandler}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                bg={"quaternary"}
                color={"tertiary"}
                _hover={{ opacity: 0.9 }}
                isLoading={isLoading}
                loadingText="Sending..."
              >
                Get Free Access
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
};

export default TrialForm;
